/**
 * MUI-native agent waiting-form renderer for eid-chatbot.
 * Replaces the external AutoFormHandler dependency.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import type { WaitingFormConfirmation } from '../types'
import { isConfirmationOnly } from '../utils/waitingFormGate'
import { ConfirmationApprovalCard } from './ConfirmationApprovalCard'

export interface AgentFormField {
    name: string
    type: string
    title: string
    description?: string
    required: boolean
    current_value?: unknown
    errors?: Array<{ code: string; message: string }>
    enum?: Array<{ value: string; label: string }>
    readOnly?: boolean
    format?: string
    redacted?: boolean
}

export interface AgentWaitingFormProps {
    taskId: string
    title: string
    description: string
    fields: AgentFormField[]
    currentValues?: Record<string, unknown>
    confirmation?: WaitingFormConfirmation
    prompt?: string
    stateVersion?: number
    onSubmit: (values: Record<string, unknown>) => void
    onCancel?: () => void
}

function transformErrorMessage(code: string, field: AgentFormField): string {
    const fieldName = field.title || field.name
    const errorMap: Record<string, string> = {
        normalize_failed:
            field.format === 'email'
                ? 'Please enter a valid email address (e.g., user@example.com)'
                : field.format === 'phone'
                  ? 'Please enter a valid phone number'
                  : `The ${fieldName.toLowerCase()} format is invalid.`,
        validation_failed: `The ${fieldName.toLowerCase()} doesn't meet the requirements.`,
        required: `${fieldName} is required.`,
        pattern_mismatch: `The ${fieldName.toLowerCase()} doesn't match the expected format.`,
        format_invalid:
            field.format === 'email'
                ? 'Please enter a valid email address'
                : field.format === 'date'
                  ? 'Please enter a valid date (YYYY-MM-DD)'
                  : `Please enter a valid ${field.format || 'value'}.`,
    }
    return errorMap[code] || `Please fix the ${fieldName.toLowerCase()}.`
}

export const AgentWaitingForm: React.FC<AgentWaitingFormProps> = ({
    taskId,
    title,
    description,
    fields,
    currentValues = {},
    confirmation,
    prompt,
    stateVersion = 1,
    onSubmit,
    onCancel,
}) => {
    const [formData, setFormData] = useState<Record<string, unknown>>({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    const confirmationOnly = isConfirmationOnly(confirmation, fields)

    useEffect(() => {
        const initial: Record<string, unknown> = {}
        for (const field of fields) {
            const value =
                field.current_value !== undefined
                    ? field.current_value
                    : currentValues[field.name]
            if (value !== undefined) {
                initial[field.name] = value
            }
        }
        setFormData(initial)
    }, [fields, currentValues])

    const requiredFields = useMemo(
        () => fields.filter((f) => f.required && f.name !== 'confirm'),
        [fields],
    )

    const completionPercentage = useMemo(() => {
        if (requiredFields.length === 0) return 100
        const filled = requiredFields.filter((f) => {
            const value = formData[f.name]
            const hasError = f.errors && f.errors.length > 0
            return value !== undefined && value !== null && value !== '' && !hasError
        })
        return Math.round((filled.length / requiredFields.length) * 100)
    }, [requiredFields, formData])

    const errorCount = useMemo(
        () => fields.filter((f) => f.errors && f.errors.length > 0).length,
        [fields],
    )

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true)
        try {
            onSubmit(formData)
        } finally {
            setIsSubmitting(false)
        }
    }, [formData, onSubmit])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault()
                if (completionPercentage === 100 && errorCount === 0) {
                    handleSubmit()
                }
            }
            if (e.key === 'Escape' && onCancel) {
                e.preventDefault()
                onCancel()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [completionPercentage, errorCount, handleSubmit, onCancel])

    if (confirmationOnly && confirmation) {
        return (
            <ConfirmationApprovalCard
                confirmation={confirmation}
                taskId={taskId}
                stateVersion={stateVersion}
                onSubmit={async (payload) => {
                    onSubmit(payload.data)
                }}
                onCancel={onCancel}
            />
        )
    }

    const renderField = (field: AgentFormField) => {
        const fieldKey = field.name
        const hasError = !!(field.errors && field.errors.length > 0)
        const errorMessage = hasError
            ? transformErrorMessage(field.errors![0].code, field)
            : undefined

        if (field.enum && field.enum.length > 0) {
            return (
                <FormControl
                    key={fieldKey}
                    fullWidth
                    size="small"
                    error={hasError}
                    sx={{ mb: 1.5 }}
                    id={`form-item-${fieldKey}`}
                >
                    <InputLabel id={`${fieldKey}-label`}>{field.title}</InputLabel>
                    <Select
                        labelId={`${fieldKey}-label`}
                        label={field.title}
                        value={formData[fieldKey] ?? ''}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [fieldKey]: e.target.value }))
                        }
                        disabled={isSubmitting || field.readOnly}
                        inputProps={{ name: fieldKey, 'aria-invalid': hasError }}
                    >
                        {field.enum.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {(errorMessage || field.description) && (
                        <FormHelperText>{errorMessage || field.description}</FormHelperText>
                    )}
                </FormControl>
            )
        }

        if (field.type === 'boolean') {
            const checkboxLabel =
                (confirmation?.confirm_message && String(confirmation.confirm_message).trim()) ||
                field.description ||
                field.title ||
                fieldKey
            return (
                <Box key={fieldKey} id={`form-item-${fieldKey}`} sx={{ mb: 1.5 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name={fieldKey}
                                checked={formData[fieldKey] === true}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        [fieldKey]: e.target.checked,
                                    }))
                                }
                                disabled={isSubmitting || field.readOnly}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 13, color: 'var(--main-text, #1e293b)' }}>
                                {checkboxLabel}
                                {field.required && (
                                    <Typography component="span" sx={{ color: 'var(--error-color, #ef4444)', ml: 0.5 }}>
                                        *
                                    </Typography>
                                )}
                            </Typography>
                        }
                    />
                    {hasError && (
                        <FormHelperText error sx={{ ml: 4 }}>
                            {errorMessage}
                        </FormHelperText>
                    )}
                </Box>
            )
        }

        const inputType =
            field.format === 'email'
                ? 'email'
                : field.type === 'number' || field.type === 'integer'
                  ? 'number'
                  : field.redacted
                    ? 'password'
                    : 'text'

        return (
            <TextField
                key={fieldKey}
                id={`form-item-${fieldKey}`}
                fullWidth
                size="small"
                name={fieldKey}
                label={field.title}
                value={formData[fieldKey] ?? ''}
                onChange={(e) =>
                    setFormData((prev) => ({ ...prev, [fieldKey]: e.target.value }))
                }
                required={field.required}
                disabled={isSubmitting || field.readOnly}
                error={hasError}
                helperText={errorMessage || field.description}
                type={inputType}
                multiline={field.type === 'text' || field.type === 'textarea'}
                minRows={field.type === 'text' || field.type === 'textarea' ? 3 : undefined}
                sx={{ mb: 1.5 }}
                inputProps={{ 'aria-invalid': hasError }}
            />
        )
    }

    const riskLevel = String(confirmation?.risk_level || '').toLowerCase()
    const hasConfirmationCopy = !!(
        confirmation?.impact_description ||
        confirmation?.confirm_message ||
        prompt
    )

    return (
        <Box
            data-testid="agent-waiting-form"
            sx={{
                borderRadius: '12px',
                border: '1px solid var(--card-border, #e2e8f0)',
                background: 'var(--card-bg, #fff)',
                p: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'var(--main-text, #1e293b)' }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--main-text-secondary, #64748b)' }}>
                    {completionPercentage}% complete
                </Typography>
            </Box>

            {description && (
                <Typography
                    sx={{ fontSize: 13, color: 'var(--main-text-secondary, #64748b)', mb: 1.5 }}
                >
                    {description}
                </Typography>
            )}

            {hasConfirmationCopy && (
                <Alert
                    severity={
                        riskLevel === 'high' || riskLevel === 'critical'
                            ? 'warning'
                            : 'info'
                    }
                    sx={{ mb: 1.5 }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                        {confirmation?.impact_description || 'Confirmation required'}
                    </Typography>
                    {(confirmation?.confirm_message || prompt) && (
                        <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                            {confirmation?.confirm_message || prompt}
                        </Typography>
                    )}
                </Alert>
            )}

            {errorCount > 0 && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {`${errorCount} ${errorCount === 1 ? 'field needs' : 'fields need'} attention`}
                </Alert>
            )}

            {fields.map(renderField)}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {onCancel && (
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        sx={{ textTransform: 'none', borderRadius: '10px' }}
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={
                        isSubmitting || completionPercentage < 100 || errorCount > 0
                    }
                    data-testid="agent-waiting-form-submit"
                    sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        background: 'var(--button-primary-bg, var(--quantum-violet, #6C4CFF))',
                    }}
                >
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                </Button>
            </Box>
        </Box>
    )
}

export default AgentWaitingForm
